import { useEnabledCompetences } from "@/api/Shifts";
import { createTag, deleteTag, tagsByGroupQueryOptions } from "@/api/Templates";
import { deducedError } from "@/Utils/ErrorUtils";
import { Trans, useLingui } from "@lingui/react/macro";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import {
  medicalCompetenceSchema,
  medicalCompetenceDictionary,
  type IMedicalCompetence,
} from "@medoma/models/shifts";
import { tagGroupsSchema } from "@medoma/models/templates";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef } from "react";
import {
  computeCompetenceTagSync,
  parseCompetenceTagName,
  sortCompetenceTagsByOrderedCompetences,
} from "./tagUtils";
import styles from "./CompetenceFilter.module.scss";

type CompetenceFilterProps = {
  filteredCompetence: IMedicalCompetence | null;
  selectCompetence: (competence: IMedicalCompetence | null) => void;
};

const CompetenceFilter = ({
  filteredCompetence,
  selectCompetence,
}: CompetenceFilterProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const hasSynced = useRef(false);
  const radioGroupName = useId();

  const {
    data: enabledCompetences,
    isPending: isPendingCompetences,
    isError: isErrorCompetences,
    error: errorCompetences,
  } = useEnabledCompetences();

  const competenceTagGroupQueryOptions = tagsByGroupQueryOptions(
    tagGroupsSchema.enum.competence,
  );

  const {
    data: competenceTags,
    isPending: isPendingTags,
    isError: isErrorTags,
    error: errorTags,
  } = useQuery(competenceTagGroupQueryOptions);

  const { mutate: syncTags, isPending: isSyncing } = useMutation({
    mutationFn: async ({
      toCreate,
      toDelete,
    }: {
      toCreate: string[];
      toDelete: string[];
    }) => {
      await Promise.all([
        ...toCreate.map((displayName) => createTag(displayName)),
        ...toDelete.map((id) => deleteTag(id)),
      ]);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: competenceTagGroupQueryOptions.queryKey,
      });
    },
  });

  useEffect(() => {
    if (!enabledCompetences || !competenceTags || hasSynced.current) {
      return;
    }
    hasSynced.current = true;

    const { toCreate, toDelete } = computeCompetenceTagSync(
      enabledCompetences,
      competenceTags,
    );

    if (toCreate.length > 0 || toDelete.length > 0) {
      syncTags({ toCreate, toDelete });
    }
  }, [enabledCompetences, competenceTags, syncTags]);

  if (isPendingCompetences || isPendingTags || isSyncing) {
    return <Loading message={t`Hämtar lista med kompetenser`} />;
  }

  if (isErrorCompetences) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta lista med kompetenser.`} ${deducedError(errorCompetences)}`}
      />
    );
  }

  if (isErrorTags) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta kompetenstaggar.`} ${deducedError(errorTags)}`}
      />
    );
  }

  const sortedCompetenceTags =
    sortCompetenceTagsByOrderedCompetences(competenceTags);

  return (
    <div
      className={styles.tabListWrapper}
      role="radiogroup"
      aria-label={t`Kompetensfilter`}
    >
      <ul className={styles.tabList}>
        <li>
          <label className={styles.tab}>
            <input
              className={styles.radio}
              type="radio"
              name={radioGroupName}
              checked={filteredCompetence === null}
              onChange={() => selectCompetence(null)}
            />
            <Trans>Alla</Trans>
          </label>
        </li>
        {sortedCompetenceTags.map(({ id, displayName }) => {
          const parsed = medicalCompetenceSchema.safeParse(
            parseCompetenceTagName(displayName),
          );
          if (!parsed.success) {
            return (
              <li key={id}>
                <label className={styles.tab}>
                  <input
                    className={styles.radio}
                    type="radio"
                    name={radioGroupName}
                    checked={false}
                    onChange={() => selectCompetence(null)}
                  />
                  {displayName}
                </label>
              </li>
            );
          }
          const label = t(medicalCompetenceDictionary[parsed.data].short);
          return (
            <li key={id}>
              <label className={styles.tab}>
                <input
                  className={styles.radio}
                  type="radio"
                  name={radioGroupName}
                  checked={filteredCompetence === parsed.data}
                  onChange={() => selectCompetence(parsed.data)}
                />
                {label}
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export { CompetenceFilter };
